import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Stack } from '@mui/material';
import { CuriosityNamespaceType } from '@procureai/curiosity-ui-mui';
import { DEFAULT_CURRENCY } from '@procureai/schemas';
import {
  CuriosityAutocompleteFieldWithController,
  CurrencyFieldWithController,
  NumberFieldWithController,
  SingleColumnFormLayout,
  TextFieldWithController,
} from '@procureai/ui-form-mui';
import { useCurrency } from '@procureai/ui-mui';
import { useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { renderCuriosityOptionWithSourceReference } from '@/legacy/util/curiosity';
import { I18nNamespace } from '~/common/types';
import { FormChangeDebouncer } from '~/components/FormChangeDebouncer/FormChangeDebouncer';
import { CommodityOrganizationSelect } from '~/modules/contractDemands/ContractDemandRequestWidget/components/CommodityOrganizationSelect/CommodityOrganizationSelect.tsx';
import { ContractDemandRequestLinePrefill } from '~/modules/contractDemands/ContractDemandRequestWidget/components/ContractDemandRequestLinePrefill/ContractDemandRequestLinePrefill.tsx';
import { TotalValue } from '~/modules/contractDemands/ContractDemandRequestWidget/components/TotalValue/TotalValue.tsx';

import { ContractDemandRequestItem, contractDemandRequestItemSchema } from './ContractDemandRequestItemForm.schema';

export type ContractDemandRequestItemFormProps = {
  viewOnly?: boolean;
  lineItem?: ContractDemandRequestItem;
  onChange: (lineItem: ContractDemandRequestItem) => void;
  validate?: boolean;
};
const commonProps = {
  size: 'small' as const,
  fullWidth: true,
};

export const ContractDemandRequestItemForm = ({
  viewOnly,
  lineItem,
  validate,
  onChange,
}: ContractDemandRequestItemFormProps) => {
  const { code } = useCurrency();
  const { t } = useTranslation(I18nNamespace.modules, { keyPrefix: 'ContractDemandRequestItemForm' });

  const form = useForm({
    resolver: zodResolver(contractDemandRequestItemSchema),
    mode: 'all',
  });

  const { control } = form;

  useEffect(() => {
    // use reset instead of values on form to not override values
    // with the onChange
    form.reset(lineItem);
    if (validate) {
      form.trigger();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const commodityIdChangeRef = useRef(false);

  return (
    <FormProvider {...form}>
      <>
        <FormChangeDebouncer control={control as any} onChange={onChange} />
        <ContractDemandRequestLinePrefill
          hasBeenChangedByUser={() => {
            if (commodityIdChangeRef.current) {
              commodityIdChangeRef.current = false;
              return true;
            }
          }}
          changeRef={commodityIdChangeRef}
        />
        <SingleColumnFormLayout>
          <CuriosityAutocompleteFieldWithController
            control={control}
            name="commodityId"
            label={t('commodityId.label')}
            ns={CuriosityNamespaceType.COMMODITY}
            renderOption={renderCuriosityOptionWithSourceReference}
            viewOnly={viewOnly}
            onChangeBefore={() => {
              commodityIdChangeRef.current = true;
            }}
            {...commonProps}
          />
          <TextFieldWithController
            control={control}
            name="title"
            label={t('title.label')}
            viewOnly={viewOnly}
            {...commonProps}
          />
          <TextFieldWithController
            control={control}
            name="description"
            label={t('description.label')}
            viewOnly={viewOnly}
            {...commonProps}
          />
          <CuriosityAutocompleteFieldWithController
            control={control}
            name="commodityGroupId"
            label={t('commodityGroup.label')}
            ns={CuriosityNamespaceType.COMMODITY_GROUP}
            renderOption={renderCuriosityOptionWithSourceReference}
            viewOnly={viewOnly}
            {...commonProps}
          />
          <Stack direction="row" gap={2} sx={{ width: '100%' }}>
            {/* 1/3 */}
            <Box sx={{ flex: '1 1 0', minWidth: 0 }}>
              <NumberFieldWithController
                control={control}
                name="quantity"
                label={t('quantity.label')}
                viewOnly={viewOnly}
                {...commonProps}
              />
            </Box>

            {/* 2/3 */}
            <Box sx={{ flex: '2 1 0', minWidth: 0 }}>
              <CuriosityAutocompleteFieldWithController
                control={control}
                name="unitId"
                label={t('unitId.label')}
                ns={CuriosityNamespaceType.UNIT}
                viewOnly={viewOnly}
                {...commonProps}
              />
            </Box>
          </Stack>
          <CommodityOrganizationSelect label={t('organizationId.label')} viewOnly={viewOnly} {...commonProps} />
          <CuriosityAutocompleteFieldWithController
            control={control}
            name="qualifiedSuppliers"
            label={t('qualifiedSuppliers.label')}
            ns={CuriosityNamespaceType.SUPPLIER}
            renderOption={renderCuriosityOptionWithSourceReference}
            multiple
            viewOnly={viewOnly}
            {...commonProps}
          />
          <NumberFieldWithController
            control={control}
            name="minimumOrderQuantity"
            label={t('minimumOrderQuantity.label')}
            viewOnly={viewOnly}
            {...commonProps}
          />
          <NumberFieldWithController
            control={control}
            name="priceUnit"
            label={t('priceUnit.label')}
            viewOnly={viewOnly}
            {...commonProps}
          />
          <CurrencyFieldWithController
            control={control}
            currency={code ?? DEFAULT_CURRENCY}
            name="baselinePricePerPriceUnit"
            label={t('baselinePricePerPriceUnit.label')}
            viewOnly={viewOnly}
            {...commonProps}
          />
          <TotalValue />
        </SingleColumnFormLayout>
      </>
    </FormProvider>
  );
};
